import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Instagram, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

export default function Footer() {
    const { t } = useLanguage();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error(t("footer.invalidEmail"));
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/newsletter`, { email });
            toast.success(t("footer.subscribed"));
            setEmail("");
        } catch {
            toast.error(t("footer.subscribeError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-black text-white" data-testid="footer">
            <div className="px-5 md:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                    <p className="font-display text-4xl md:text-6xl font-extrabold uppercase tracking-[0.2em]">
                        SEVA
                    </p>
                    <p className="mt-6 text-sm text-zinc-400 max-w-sm leading-relaxed">
                        {t("footer.tagline")}
                    </p>
                    <a
                        href="https://instagram.com/seva_wear"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-instagram"
                        className="mt-8 inline-flex items-center gap-3 border border-zinc-700 px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 hover:bg-white hover:text-black"
                    >
                        <Instagram size={16} />
                        {t("footer.instagram")}
                    </a>
                </div>

                <div className="flex flex-col justify-center">
                    <p className="text-xs tracking-[0.3em] uppercase text-zinc-400 mb-4">{t("footer.newsletter")}</p>
                    <form onSubmit={handleSubscribe} className="flex border-b border-zinc-700 pb-3">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("footer.placeholder")}
                            data-testid="newsletter-email-input"
                            className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-600"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="newsletter-submit-button"
                            aria-label="Iscriviti"
                            className="group flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold"
                        >
                            {loading ? "..." : t("footer.subscribe")}
                            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </form>

                    <div className="mt-12 flex gap-8 text-xs tracking-[0.25em] uppercase text-zinc-400">
                        <Link to="/shop" className="hover:text-white transition-colors" data-testid="footer-shop">Shop</Link>
                        <Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about">About</Link>
                        <Link to="/lookbook" className="hover:text-white transition-colors" data-testid="footer-lookbook">Lookbook</Link>
                    </div>
                </div>
            </div>
            <div className="border-t border-zinc-800 px-5 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                <span>© {new Date().getFullYear()} Seva — {t("footer.rights")}</span>
                <span>{t("footer.madeIn")}</span>
            </div>
        </footer>
    );
}
